import { KIND, SHAPE } from 'baseui/button'
import { CheckIndeterminate, Plus } from 'baseui/icon'
import { Slider } from 'baseui/slider'
import { styled } from 'baseui'
import ButtonCustom from '@/components/ButtonCustom'
import { SizeButton } from '@/constants/sizeButton'
import Icons from '@/scenes/Editor/components/Icons'
import { Z_INDEX } from '@/constants/z-index'
import { lightTheme } from '@/customTheme'

/** Styletron starts the animation when the class appears, so it replays each time Reset shows. */
const ResetSlot = styled('div', ({ $animate }: { $animate: boolean }) => ({
  display: 'flex',
  ...($animate ? {
    animationName: { from: { opacity: 0, transform: 'scale(.88)' }, to: { opacity: 1, transform: 'scale(1)' } },
    animationDuration: '160ms',
    animationTimingFunction: 'ease-out',
    '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
  } : {}),
}))

type ZoomControlsProps = {
  value: number
  min: number
  max: number
  step: number
  disabled?: boolean
  showReset?: boolean
  trackPercent?: number
  onReset: () => void
  onZoomOut: () => void
  onZoomIn: () => void
  onChange: (value: number) => void
  /** Tooltip copy; defaults keep the editor's original strings. */
  labels?: { reset?: string; zoomOut?: string; zoomIn?: string }
  /** Drops the slider so a tight toolbar keeps only the buttons. */
  hideSlider?: boolean
  /** Disables minus at `min` and plus at `max`. Only for callers whose `value` shares their units. */
  disableAtLimits?: boolean
  /** Scales and fades Reset in when it appears. */
  animateReset?: boolean
  /** Drops the white pill, for bars whose own surface already frames the controls. */
  transparent?: boolean
}

function ZoomControls({
  value,
  min,
  max,
  step,
  disabled = false,
  showReset = false,
  trackPercent: trackPercentOverride,
  onReset,
  onZoomOut,
  onZoomIn,
  onChange,
  labels,
  hideSlider = false,
  disableAtLimits = false,
  animateReset = false,
  transparent = false,
}: ZoomControlsProps) {
  const calculatedTrackPercent = max === min
    ? 0
    : Math.min(Math.max(((value - min) / (max - min)) * 100, 0), 100)
  const trackPercent = trackPercentOverride ?? calculatedTrackPercent
  const zoomOutDisabled = disabled || (disableAtLimits && value <= min)
  const zoomInDisabled = disabled || (disableAtLimits && value >= max)

  return (
    <div
      style={{
        position: 'relative',
        zIndex: Z_INDEX.MAIN_ZOOM_CONTROLS,
        boxSizing: 'border-box',
        background: transparent ? 'transparent' : '#fff',
        height: '40px',
        display: 'flex',
        gap: '8px',
        borderRadius: '8px',
        padding: '4px',
        alignItems: 'center',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <ResetSlot $animate={animateReset && showReset}>
      <ButtonCustom
        kind={KIND.minimal}
        type={SizeButton.STANDARD}
        shape={SHAPE.square}
        onClick={onReset}
        tooltip={{ content: labels?.reset ?? 'Reset zoom', placement: 'top' }}
        style={{
          display: 'flex',
          borderRadius: '50%',
          visibility: showReset ? 'visible' : 'hidden',
          opacity: 1,
          pointerEvents: disabled || !showReset ? 'none' : 'auto',
        }}
      >
        <Icons.ResetZoomIcon disabled={disabled} />
      </ButtonCustom>
      </ResetSlot>
      <ButtonCustom
        kind={KIND.minimal}
        type={SizeButton.STANDARD}
        shape={SHAPE.square}
        onClick={onZoomOut}
        disabled={zoomOutDisabled}
        tooltip={{ content: labels?.zoomOut ?? 'Zoom out', placement: 'top' }}
        style={{
          // A disabled <button> can swallow mouseup, which strands a slider
          // drag released over it; let the event fall through to the cluster.
          pointerEvents: zoomOutDisabled ? 'none' : 'auto',
          borderRadius: '50%',
        }}
      >
        <CheckIndeterminate color={zoomOutDisabled ? lightTheme.colors.icon.disable : lightTheme.colors.blackGray} size={24} title="" />
      </ButtonCustom>
      {!hideSlider && <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '72px',
          margin: '0 4px',
          alignItems: 'center',
        }}
      >
        <Slider
          value={[value]}
          min={min}
          max={max}
          step={step}
          onChange={({ value: nextValue }) => nextValue && onChange(nextValue[0])}
          overrides={{
            Track: { style: () => ({ paddingLeft: '0', paddingRight: '0' }) },
            InnerThumb: () => <></>,
            ThumbValue: () => null,
            Thumb: {
              style: () => ({
                height: '20px',
                width: '20px',
                borderRadius: '50%',
                boxShadow: '0px 0px 6px rgba(0,0,0,.3)',
                backgroundColor: '#fff',
              }),
            },
            InnerTrack: {
              style: () => ({
                background: `linear-gradient(to right, ${lightTheme.colors.blackGray} 0%, ${lightTheme.colors.grayScale600} ${trackPercent}%, ${lightTheme.colors.grayScale100} ${trackPercent}%, ${lightTheme.colors.grayScale100} 100%)`,
                width: '100%',
              }),
            },
            Tick: { style: () => ({ display: 'none' }) },
            TickBar: { style: () => ({ display: 'none' }) },
          }}
          disabled={disabled}
        />
      </div>}
      <ButtonCustom
        kind={KIND.minimal}
        type={SizeButton.STANDARD}
        shape={SHAPE.square}
        onClick={onZoomIn}
        disabled={zoomInDisabled}
        tooltip={{ content: labels?.zoomIn ?? 'Zoom in', placement: 'top' }}
        style={{
          pointerEvents: zoomInDisabled ? 'none' : 'auto',
          borderRadius: '50%',
        }}
      >
        <Plus color={zoomInDisabled ? lightTheme.colors.icon.disable : lightTheme.colors.blackGray} size={24} title="" />
      </ButtonCustom>
    </div>
  )
}

export default ZoomControls
